// Displays the user's collection and owns collection-page UI state only.
// The canonical card list lives in App.js and arrives through `cards`.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmModal from "../../ConfirmModal";
import { supabase } from "../../supabaseClient";
import CardDetailModal from "../../CardDetailModal";
import CardFormWrapper from "../../CardFormWrapper";
import ItemListHeader from "./ItemListHeader";
import ItemGrid from "./ItemGrid";
import { deleteItem } from "../../services/deleteItem";
import "./ItemList.css";

const DEFAULT_SORT_CONFIG = [
  { field: "year", direction: "asc" },
  { field: "", direction: "asc" },
  { field: "", direction: "asc" },
];

const ITEM_LIST_PREFERENCE_KEY = "hobbli:item-list-preferences:v1";

function readSavedPreferences() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(ITEM_LIST_PREFERENCE_KEY) || "{}");
  } catch {
    return {};
  }
}

function ItemList({
  cards = [],
  currentUser,
  collections = [],
  onDeleteItem,
  onCardsChanged,
  onCollectionsChanged,
  showHobbliVision,
  setShowHobbliVision,
}) {
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [pendingDeleteCardId, setPendingDeleteCardId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [cardFormData, setCardFormData] = useState({});
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [batchDelete, setBatchDelete] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showManageCollections, setShowManageCollections] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [deleting, setDeleting] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [subcategories, setSubcategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [uiNotice, setUiNotice] = useState(null);

  const lastSelectedIndex = useRef(null);
  const longPressTimer = useRef(null);

  // Remember the collection-page presentation for this signed-in user on this browser.
  // Filters stay session-only; view, page size, sorting, and table columns are preferences.
  useEffect(() => {
    const saved = readSavedPreferences();

    if ([10, 20, 50].includes(Number(saved.itemsPerPage))) {
      setItemsPerPage(Number(saved.itemsPerPage));
    }

    if (Array.isArray(saved.sortConfig) && saved.sortConfig.length) {
      setSortConfig(saved.sortConfig);
    }

    setPreferencesLoaded(true);
  }, []);

  useEffect(() => {
    if (!preferencesLoaded || typeof window === "undefined") return;

    const key = ITEM_LIST_PREFERENCE_KEY;
    let existing = {};
    try {
      existing = JSON.parse(window.localStorage.getItem(key) || "{}");
    } catch {
      existing = {};
    }

    window.localStorage.setItem(
      key,
      JSON.stringify({
        ...existing,
        viewMode,
        itemsPerPage,
        sortConfig,
      })
    );
  }, [preferencesLoaded, viewMode, itemsPerPage, sortConfig]);

  const selectedCard = useMemo(() => {
    if (selectedCardId == null) return null;
    return (
      cards.find((card) => String(card.id) === String(selectedCardId)) || null
    );
  }, [cards, selectedCardId]);

  const pendingDeleteCard = useMemo(() => {
    if (pendingDeleteCardId == null) return null;
    return (
      cards.find((card) => String(card.id) === String(pendingDeleteCardId)) ||
      null
    );
  }, [cards, pendingDeleteCardId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .order("name", { ascending: true });

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!currentUser?.id) {
      setUserCategories([]);
      return;
    }

    const fetchUserCategories = async () => {
      try {
        const { data, error } = await supabase.rpc("get_user_categories", {
          p_user_id: currentUser.id,
        });
        if (error) throw error;

        setUserCategories(
          [...(data || [])].sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        console.error("Error fetching user categories:", err);
        setUserCategories([]);
      }
    };

    fetchUserCategories();
  }, [currentUser?.id]);

  // Names come from the taxonomy table, but available options are derived
  // from cards the user actually owns (never from the entire taxonomy).
  useEffect(() => {
    let active = true;
    const fetchSubcategories = async () => {
      const { data, error } = await supabase
        .from("subcategories")
        .select("id, name, category_id")
        .order("name", { ascending: true });
      if (error) {
        console.error("Error fetching subcategories:", error);
        return;
      }
      if (active) setSubcategories(data || []);
    };
    fetchSubcategories();
    return () => { active = false; };
  }, []);

  const userSubcategories = useMemo(() => {
    const byId = new Map(subcategories.map((sub) => [String(sub.id), sub]));
    const owned = new Map();
    cards.forEach((card) => {
      const id = card.subcategory_id;
      if (id == null || id === "") return;
      const key = String(id);
      const taxonomy = byId.get(key);
      const name = taxonomy?.name || card.subcategory_name ||
        (typeof card.subcategory === "string" && card.subcategory !== key ? card.subcategory : null);
      if (!name) return;
      owned.set(key, {
        id: key,
        name,
        category_id: String(card.category_id ?? taxonomy?.category_id ?? ""),
      });
    });
    return [...owned.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [cards, subcategories]);

  const availableSubcategories = useMemo(() =>
    selectedCategory === "all"
      ? userSubcategories
      : userSubcategories.filter((sub) => sub.category_id === String(selectedCategory)),
    [userSubcategories, selectedCategory]
  );

  const changeCategory = (value) => {
    setSelectedCategory(value);
    if (selectedSubcategory !== "all" && value !== "all") {
      const current = userSubcategories.find((sub) => sub.id === String(selectedSubcategory));
      if (!current || current.category_id !== String(value)) setSelectedSubcategory("all");
    }
  };

  const changeSubcategory = (value) => {
    setSelectedSubcategory(value);
    if (value !== "all" && selectedCategory !== "all") {
      const sub = userSubcategories.find((entry) => entry.id === String(value));
      if (sub && sub.category_id !== String(selectedCategory)) setSelectedCategory("all");
    }
  };

  // Remove selections that no longer exist after deletes / refreshes.
  useEffect(() => {
    const validIds = new Set(cards.map((card) => String(card.id)));
    setSelectedCards((prev) =>
      prev.filter((id) => validIds.has(String(id)))
    );

    if (selectedCardId != null && !validIds.has(String(selectedCardId))) {
      setSelectedCardId(null);
      setShowDetailModal(false);
      setEditMode(false);
    }
  }, [cards, selectedCardId]);

  useEffect(
    () => () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    },
    []
  );

  const refreshCards = async () => {
    if (onCardsChanged) {
      return onCardsChanged();
    }
    return null;
  };

  const refreshCollections = async () => {
    if (onCollectionsChanged) {
      return onCollectionsChanged();
    }
    return null;
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!currentUser?.id) return;

    try {
      const { error } = await supabase.rpc("delete_collection", {
        p_collection_id: Number(collectionId),
      });
      if (error) throw error;

      if (String(selectedCollection) === String(collectionId)) {
        setSelectedCollection("all");
      }

      await Promise.all([refreshCollections(), refreshCards()]);
    } catch (err) {
      console.error("Failed to delete collection:", err);
      setUiNotice({ type: "error", message: "Failed to delete collection. Please try again." });
    }
  };

  const handleDeleteClick = (singleCard = null, batch = false) => {
    if (batch && selectedCards.length === 0) return;

    setBatchDelete(batch);
    setPendingDeleteCardId(batch ? null : singleCard?.id ?? null);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    const ids = batchDelete
      ? selectedCards
      : pendingDeleteCardId != null
        ? [pendingDeleteCardId]
        : [];

    if (ids.length === 0) {
      setShowDeleteConfirm(false);
      return;
    }

    try {
      setDeleting(true);

      if (onDeleteItem) {
        await onDeleteItem(ids.length === 1 ? ids[0] : ids);
      } else {
        for (const id of ids) await deleteItem(id);
        await refreshCards();
      }

      if (
        selectedCardId != null &&
        ids.some((id) => String(id) === String(selectedCardId))
      ) {
        setSelectedCardId(null);
        setShowDetailModal(false);
        setEditMode(false);
      }

      setSelectedCards([]);
      setPendingDeleteCardId(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Delete process error:", error);
      setUiNotice({ type: "error", message: "Failed to delete the selected item(s). Please try again." });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteCardId(null);
    setBatchDelete(false);
  };

  const handleEditSaved = async () => {
    // Refresh the canonical collection data, but keep the same card selected so
    // the user returns to its detail modal after saving.
    await refreshCards();
    setEditMode(false);
    setShowDetailModal(true);
    setCardFormData({});
  };

  const toggleSelectCard = (cardId) => {
    setSelectedCards((prev) =>
      prev.some((id) => String(id) === String(cardId))
        ? prev.filter((id) => String(id) !== String(cardId))
        : [...prev, cardId]
    );
  };

  const handleCardClick = (index, card, event) => {
    if (multiSelectMode || event?.shiftKey) {
      let newSelected = [...selectedCards];

      if (event?.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        const idsInRange = sortedCards.slice(start, end + 1).map((item) => item.id);

        idsInRange.forEach((id) => {
          if (!newSelected.some((selectedId) => String(selectedId) === String(id))) {
            newSelected.push(id);
          }
        });
      } else if (
        newSelected.some((id) => String(id) === String(card.id))
      ) {
        newSelected = newSelected.filter(
          (id) => String(id) !== String(card.id)
        );
      } else {
        newSelected.push(card.id);
      }

      setSelectedCards(newSelected);
      lastSelectedIndex.current = index;
      setMultiSelectMode(true);
      return;
    }

    setSelectedCardId(card.id);
    setShowDetailModal(true);
  };

  const handleTouchStart = (index, card) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);

    longPressTimer.current = setTimeout(() => {
      setMultiSelectMode(true);
      toggleSelectCard(card.id);
      lastSelectedIndex.current = index;
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleAddToCollection = async (collectionId = null, createNew = false) => {
    if (!currentUser?.id || selectedCards.length === 0) return;

    let targetCollectionId = collectionId;

    try {
      if (createNew && newCollectionName.trim()) {
        const { data: newCollection, error } = await supabase
          .from("collections")
          .insert([{ name: newCollectionName.trim(), user_id: currentUser.id }])
          .select()
          .single();

        if (error) throw error;
        targetCollectionId = newCollection.id;
      }

      if (!targetCollectionId) return;

      const payload = selectedCards.map((cardId) => ({
        collection_id: targetCollectionId,
        user_item_id: cardId,
      }));

      const { error } = await supabase
        .from("collection_submissions")
        .insert(payload);
      if (error) throw error;

      setSelectedCards([]);
      setShowCollectionModal(false);
      setNewCollectionName("");

      await Promise.all([refreshCollections(), refreshCards()]);
    } catch (err) {
      console.error("Failed to add cards to collection:", err);
      setUiNotice({ type: "error", message: "Failed to add the selected item(s) to the collection. Please try again." });
    }
  };

  const sortedCards = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return [...cards]
      .filter((card) => {
        const matchCollection =
          selectedCollection === "all" ||
          (card.collection_ids || []).some(
            (collectionId) =>
              String(collectionId) === String(selectedCollection)
          );

        const matchCategory =
          selectedCategory === "all" ||
          String(card.category_id ?? "") === String(selectedCategory) ||
          String(card.category ?? "") === String(selectedCategory);

        const matchSubcategory =
          selectedSubcategory === "all" ||
          String(card.subcategory_id ?? "") === String(selectedSubcategory);

        const matchSearch =
          !search ||
          [
            card.name,
            card.brand,
            card.set_name,
            card.subset,
            card.year,
            card.team,
            card.notes,
          ].some((value) =>
            String(value ?? "").toLowerCase().includes(search)
          );

        return matchCollection && matchCategory && matchSubcategory && matchSearch;
      })
      .sort((a, b) => {
        for (const sort of sortConfig) {
          if (!sort.field) continue;

          const aVal = a[sort.field];
          const bVal = b[sort.field];

          if (aVal == null && bVal == null) continue;
          if (aVal == null) return 1;
          if (bVal == null) return -1;

          const aNum = Number(aVal);
          const bNum = Number(bVal);
          const bothNumeric =
            Number.isFinite(aNum) &&
            Number.isFinite(bNum) &&
            aVal !== "" &&
            bVal !== "";

          if (bothNumeric) {
            if (aNum < bNum) return sort.direction === "asc" ? -1 : 1;
            if (aNum > bNum) return sort.direction === "asc" ? 1 : -1;
            continue;
          }

          const comparison = String(aVal)
            .toLowerCase()
            .localeCompare(String(bVal).toLowerCase());

          if (comparison !== 0) {
            return sort.direction === "asc" ? comparison : -comparison;
          }
        }

        return 0;
      });
  }, [cards, selectedCollection, selectedCategory, selectedSubcategory, searchTerm, sortConfig]);

  const beginEdit = () => {
    if (!selectedCard) return;

    const normalizedVariants = Array.isArray(selectedCard.variants)
      ? selectedCard.variants.map((variant) =>
          typeof variant === "string" ? variant : variant?.name
        ).filter(Boolean)
      : typeof selectedCard.variants === "string"
        ? selectedCard.variants
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [];

    const cardNumber =
      selectedCard.number ?? selectedCard.card_number ?? "";

    setCardFormData({
      ...selectedCard,
      name: selectedCard.name ?? selectedCard.suggested_name ?? "",
      suggested_name: selectedCard.suggested_name ?? selectedCard.name ?? "",
      subset: selectedCard.subset ?? "",
      variants: normalizedVariants,
      variations: normalizedVariants.map((name) => ({ name, canonical: true })),
      number: cardNumber,
      card_number: cardNumber,
      cardNumber,
    });

    // Keep the detail modal mounted underneath the edit form. When editing
    // finishes, closing the form reveals the same card instead of returning
    // the user to the collection.
    setEditMode(true);
  };

  const isDarkMode = document.body.classList.contains("dark-mode");

  return (
    <div>
      {uiNotice ? (
        <div role="status" style={{ margin: "0 0 14px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220, 53, 69, 0.10)" : "rgba(47, 128, 237, 0.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>
          {uiNotice.message}
        </div>
      ) : null}
      <ItemListHeader
        collections={collections}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        selectedCategory={selectedCategory}
        setSelectedCategory={changeCategory}
        selectedSubcategory={selectedSubcategory}
        setSelectedSubcategory={changeSubcategory}
        userSubcategories={availableSubcategories}
        categories={categories}
        userCategories={userCategories}
        setShowAddCardModal={setShowAddCardModal}
        multiSelectMode={multiSelectMode}
        setMultiSelectMode={setMultiSelectMode}
        selectedCards={selectedCards}
        setSelectedCards={setSelectedCards}
        handleDeleteClick={handleDeleteClick}
        setShowCollectionModal={setShowCollectionModal}
        setShowManageCollections={setShowManageCollections}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        viewMode={viewMode}
        setViewMode={setViewMode}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showHobbliVision={showHobbliVision}
        setShowHobbliVision={setShowHobbliVision}
      />

      <ItemGrid
        filteredCards={sortedCards}
        multiSelectMode={multiSelectMode}
        selectedCards={selectedCards}
        setSelectedCards={setSelectedCards}
        handleCardClick={handleCardClick}
        handleTouchStart={handleTouchStart}
        handleTouchEnd={handleTouchEnd}
        viewMode={viewMode}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        preferenceKey={ITEM_LIST_PREFERENCE_KEY}
      />

      {showAddCardModal && (
        <div className={`modal-overlay card-form-overlay ${isDarkMode ? "dark" : ""}`}>
          <div className="modal-content card-form-modal">
          <CardFormWrapper
            onAddItem={async () => {
              await refreshCards();
              setShowAddCardModal(false);
            }}
            onCancel={() => setShowAddCardModal(false)}
            user={currentUser}
          />
          </div>
        </div>
      )}

      {showDetailModal && selectedCard && (
        <div className="modal-overlay card-detail-overlay">
          <CardDetailModal
            card={selectedCard}
            isDarkMode={isDarkMode}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedCardId(null);
            }}
            onEdit={beginEdit}
            onDelete={() => {
              setShowDetailModal(false);
              handleDeleteClick(selectedCard, false);
            }}
            onImagesChanged={refreshCards}
          />
        </div>
      )}

      {editMode && selectedCard && (
        <div className="modal-overlay card-form-overlay">
          <div className="modal-content card-form-modal">
            <CardFormWrapper
              cardData={cardFormData}
              isEdit
              onAddItem={handleEditSaved}
              onCancel={() => {
                // Cancel only the edit layer and return to the same detail card.
                setEditMode(false);
                setShowDetailModal(true);
                setCardFormData({});
              }}
              user={currentUser}
            />
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <ConfirmModal
              message={
                batchDelete
                  ? `Delete ${selectedCards.length} selected cards?`
                  : `Delete ${pendingDeleteCard?.name || "this card"}?`
              }
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
              disabled={deleting}
            />
          </div>
        </div>
      )}

      {showManageCollections && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Manage Collections</h3>
            <ul>
              {collections.map((collection) => (
                <li key={collection.id}>
                  {collection.name}
                  <button
                    onClick={() => handleDeleteCollection(collection.id)}
                    style={{
                      backgroundColor: "#e63946",
                      color: "white",
                      border: "none",
                      padding: "4px 8px",
                      marginLeft: "10px",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
            <button onClick={() => setShowManageCollections(false)}>Close</button>
          </div>
        </div>
      )}

      {showCollectionModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add to Collection</h3>

            <select
              value=""
              onChange={(event) =>
                handleAddToCollection(Number(event.target.value))
              }
              className="form-select"
            >
              <option value="" disabled>
                Select a collection
              </option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.name}
                </option>
              ))}
            </select>

            <div style={{ margin: "10px 0", fontWeight: "bold" }}>
              Or create new:
            </div>
            <input
              type="text"
              placeholder="New collection name"
              value={newCollectionName}
              onChange={(event) => setNewCollectionName(event.target.value)}
              className="form-input"
            />

            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                className="btn-primary"
                onClick={() => handleAddToCollection(null, true)}
              >
                Create & Add
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowCollectionModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemList;
